/**
 * Radio log: ATC transmissions and pilot replies, newest at the bottom.
 * Audio providers and microphone lifecycle are owned by the host app.
 */
export interface RadioLine {
  id: number; timeS: number; from: 'atc' | 'pilot'; callsign: string; text: string; speech: string; kind: string;
  /** What a controller line did to the flight: shown under it, so "roger" to nothing is visible. */
  effect?: { tone: 'done' | 'talk' | 'refused'; text: string };
  /** Which brain chose a pilot line's words; shown only when the log is in debug mode. */
  brain?: { source: string; reason?: string };
}

const MARK = { done: '✓', talk: '·', refused: '✗' } as const;

export interface CommsLog {
  push(msg: RadioLine): void;
  destroy(): void;
}

export function createCommsLog(container: HTMLElement, options: { maxLines?: number; debug?: boolean } = {}): CommsLog {
  container.classList.add('rs-comms');
  container.classList.toggle('rs-debug', !!options.debug);
  const list = document.createElement('div');
  list.className = 'rs-comms-list';
  container.appendChild(list);
  const maxLines = options.maxLines ?? 120;
  const fmt = (t: number) => `${String(Math.floor(t / 60) % 60).padStart(2, '0')}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

  return {
    push(msg) {
      const line = document.createElement('div');
      line.className = `rs-line ${msg.from} ${msg.kind}`;
      line.innerHTML = `<span class="t">${fmt(msg.timeS)}</span><span class="who">${msg.from === 'atc' ? 'TWR' : msg.callsign}</span><span class="txt"></span>`;
      line.querySelector('.txt')!.textContent = msg.text;
      if (msg.effect) {
        const fx = document.createElement('span');
        fx.className = `fx ${msg.effect.tone}`;
        fx.textContent = `${MARK[msg.effect.tone]} ${msg.effect.text}`;
        line.appendChild(fx);
      }
      if (msg.brain && msg.from === 'pilot') {
        const b = document.createElement('span');
        b.className = `brain ${msg.brain.source}`;
        b.textContent = msg.brain.reason ? `${msg.brain.source}: ${msg.brain.reason}` : msg.brain.source;
        line.appendChild(b);
      }
      list.appendChild(line);
      while (list.children.length > maxLines) list.firstElementChild?.remove();
      list.scrollTop = list.scrollHeight;
    },
    destroy() { container.replaceChildren(); container.classList.remove('rs-comms'); },
  };
}
